import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { PHONE } from "./shared";

const inputCls =
  "w-full border border-stone-700 bg-[#0c0a09] px-4 py-3 text-base sm:text-sm text-bone placeholder:text-stone-600 focus:border-amber-500 focus:outline-none transition-colors duration-150";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";
const checkCls = "mt-0.5 h-4 w-4 shrink-0 accent-amber-500";
const checkRowCls =
  "flex min-h-[48px] cursor-pointer items-center gap-3 border border-stone-800 bg-[#0c0a09] px-3.5 py-3 text-sm text-stone-300 transition-colors duration-150 hover:border-stone-600";

const PRIVATE_LINE_OPTIONS = [
  { key: "irrigation", label: "Irrigation / sprinkler system" },
  { key: "pet_fence", label: "Invisible pet fence" },
  { key: "landscape_lighting", label: "Low-voltage landscape lighting" },
  { key: "septic", label: "Septic line" },
  { key: "other", label: "Something else nearby we should know about" },
  { key: "none", label: "None that I know of" },
];

export default function TermsAcceptance({ terms, onChange }) {
  const [lineChecks, setLineChecks] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [photoOptOut, setPhotoOptOut] = useState(false);

  const toggleLine = (key) => {
    setLineChecks((prev) => {
      if (key === "none") return prev.includes("none") ? [] : ["none"];
      const without = prev.filter((k) => k !== "none");
      return without.includes(key) ? without.filter((k) => k !== key) : [...without, key];
    });
  };

  useEffect(() => {
    onChange({
      agreed_terms: agreed,
      private_lines: lineChecks,
      private_lines_other: lineChecks.includes("other") ? otherText.trim() : null,
      photo_opt_out: photoOptOut,
      valid: Boolean(
        agreed && lineChecks.length > 0 && (!lineChecks.includes("other") || otherText.trim())
      ),
    });
  }, [agreed, lineChecks, otherText, photoOptOut, onChange]);

  return (
    <>
      <div>
        <p className={labelCls}>Terms of service *</p>
        <div data-testid="terms-panel" className="max-h-56 space-y-3 overflow-y-auto border border-stone-700 bg-[#0c0a09] p-4 text-xs leading-relaxed text-stone-400">
          {terms ? (
            <>
              <p className="font-display text-sm font-bold uppercase text-bone">{terms.title}</p>
              <p>{terms.intro}</p>
              {terms.sections.map((s) => (
                <p key={s.heading}>
                  <span className="font-semibold text-stone-200">{s.heading}.</span> {s.body}
                </p>
              ))}
              <p className="text-stone-500">{terms.closing}</p>
            </>
          ) : (
            <p data-testid="terms-loading" className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading terms… (if this sticks, refresh or text {PHONE})
            </p>
          )}
        </div>
      </div>

      <fieldset>
        <legend className={labelCls}>Before we grind — do you have any of these on your property? *</legend>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">
          Check all that apply — 811 marks public lines, but never private ones
        </p>
        <div className="space-y-2">
          {PRIVATE_LINE_OPTIONS.map((o) => (
            <label key={o.key} data-testid={`private-lines-${o.key}`} className={checkRowCls}>
              <input type="checkbox" checked={lineChecks.includes(o.key)} onChange={() => toggleLine(o.key)} className={checkCls} />
              {o.label}
            </label>
          ))}
        </div>
        {lineChecks.includes("other") && (
          <input
            data-testid="private-lines-other-input"
            className={`${inputCls} mt-2`}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Tell us what & where (e.g., gas line to the grill)"
          />
        )}
      </fieldset>

      <label data-testid="terms-agree-label" className={checkRowCls}>
        <input data-testid="terms-agree-checkbox" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className={checkCls} />
        <span>I have read and agree to the terms above <span className="text-amber-500">*</span></span>
      </label>
      <label data-testid="photo-optout-label" className={checkRowCls}>
        <input data-testid="photo-optout-checkbox" type="checkbox" checked={photoOptOut} onChange={(e) => setPhotoOptOut(e.target.checked)} className={checkCls} />
        Please don't use photos of my property for marketing
      </label>
    </>
  );
}
